import React, { useState, useMemo, useRef, useEffect, FC } from 'react';
import styled from '@emotion/styled';
import { Box, Input, SearchInputWrapper } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useLogger, useSelector } from '~Hooks';
import { useTableUniverse } from '~Components/TableUniverse';
import { useStorage } from '~Components/Storage';
import useI18n from '~Translations';
import IncomingBookings from './Components/IncomingBookings';
import TableRow from './Components/TableRow';
import ChequeDetails from './Components/ChequeDetails';
import BookingDetails from './Components/BookingDetails';
import DateInput from '~ComponentsImproved/DateInput';
import Toggle from '~ComponentsImproved/Toggle';
import { BookingStatus } from '../../../__queries__/globalTypes';
import Loader from '~ComponentsImproved/Loader';
import {
  calculateDynamicTimeRange,
  getCurrentTimePosition,
  isToday,
  scrollToCurrentTime,
} from './utils';
import { useBookings } from '~Providers/BookingsProvider';
import { DateTime } from 'luxon';
import {
  GetBookings_operativeViewer_bookingsForVenueAndDate,
  GetIncomingBookings_operativeViewer_incomingBookingsForVenue,
} from '~Providers/BookingsProvider/api/__queries__';
import Fuse from 'fuse.js';
import NewBooking from './Components/NewBooking';
import { useBookingsForDate } from './useBookingsForDate';

const OuterContainer = styled(Box)`
  overflow: hidden;
  height: 100%;
  width: calc(100vw - 400px - 176px);
`;

const Container = styled(Box)`
  padding: 24px;
  height: 100%;
  overflow-y: auto;
  width: 100%;
  flex: 1;
`;

const DateNavigationBox = styled(Box)`
  width: 270px;
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 0;
  overflow: auto;
  max-height: calc(100% - 120px);
  position: relative;
`;

const TimelineScrollArea = styled.div`
  position: relative;
  min-width: min-content;
`;

const CurrentTimeIndicator = styled.div<{ gridColumn: number; offsetPercentage: number }>`
  position: absolute;
  top: 32px; /* Start below the sticky header */
  bottom: 0;
  width: 2px;
  background-color: var(--colorUIYellow);
  box-shadow: 0 0 8px rgba(251, 191, 36, 0.6);
  z-index: 6; /* Above booking blocks (z-index: 5) but below the sticky header (z-index: 20) */
  pointer-events: none;

  /* Position based on grid column and percentage offset within the column */
  left: calc(
    64px + (${(props) => props.gridColumn - 2} * 61px) +
      (${(props) => props.offsetPercentage} * 61px / 100)
  );

  &::before {
    content: '';
    position: absolute;
    top: -32px; /* Extend to the top of the timeline including header */
    left: 0;
    width: 2px;
    height: 32px;
    background-color: var(--colorUIYellow);
  }

  &::after {
    content: '';
    position: absolute;
    top: -36px; /* Position dot at the very top */
    left: -4px;
    width: 10px;
    height: 10px;
    background-color: var(--colorUIYellow);
    border-radius: 50%;
  }
`;

const TimelineHeader = styled.div<{ columnCount: number }>`
  display: grid;
  grid-template-columns: 64px repeat(${(props) => props.columnCount}, 61px);
  position: sticky;
  top: 0;
  z-index: 20;
  min-width: min-content;
  background: var(--colorBlueGray1100);
`;

const TimeSlot = styled.div`
  padding: 11px 0 0 0;
  color: var(--colorBlueGray200);
  font-size: 11px;
  font-weight: 500;
  background-color: var(--colorBlueGray1100);
`;

const TableLabel = styled.div`
  padding: 8px;
  color: var(--colorWhite);
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  background-color: var(--colorBlueGray1100);
  position: sticky;
  left: 0;
  z-index: 25;
`;

const EmptyState = styled(Box)`
  text-align: center;
  padding: 48px 24px;
`;

const RightWrapper = styled.div`
  position: fixed;
  top: 24px;
  right: 0;
  height: calc(100% - 24px);
  width: 400px;
  overflow-y: auto;
`;

const SearchInputBox = styled(SearchInputWrapper)`
  width: 240px;
`;

interface Props {
  onNavigateToTable: (tableId: string) => void;
}

interface BookingSearchEntry {
  id: string;
  guestName: string;
}

// Helper function to check if two time ranges overlap
const Bookings: FC<Props> = ({ onNavigateToTable }) => {
  const { i18n } = useI18n();
  const logger = useLogger('bookings');

  const floorPlans = useSelector((state) => state.floorPlans);
  const tables = floorPlans.flatMap((fp) => fp.tables) ?? [];

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BookingSearchEntry[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // State for managing booking placement workflow
  const [bookingToHighlight, setBookingToHighlight] = useState<string | null>(null);
  // State for auto-selecting a newly created booking
  const [bookingToAutoSelect, setBookingToAutoSelect] = useState<string | null>(null);
  // State for tracking current time - updates every minute
  const [currentTime, setCurrentTime] = useState(new Date());

  // State for selected cheque to show details
  const [selectedCheque, setSelectedCheque] = useState<{
    chequeId: string;
    tableId: string;
  } | null>(null);

  // State for selected booking to show details
  const [
    selectedBooking,
    setSelectedBooking,
  ] = useState<GetBookings_operativeViewer_bookingsForVenueAndDate | null>(null);

  // Storage for persistent settings including hideEmptyTables toggle
  const [storage, updateStorage] = useStorage();

  const bookings = useBookings();

  const bookingFuse = useRef(
    new Fuse<BookingSearchEntry>([], {
      keys: ['guestName'],
      includeScore: true,
      threshold: 0,
      ignoreLocation: true,
    }),
  );

  const selectedDateBookings = useBookingsForDate(selectedDate);

  // Ref for timeline container to enable scrolling
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  // Ref to track if initial scroll to current time has occurred
  const hasScrolledToCurrentTime = useRef(false);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Navigate to date when an incoming booking is clicked from the sidebar
  useEffect(() => {
    if (!bookings.highlightedIncomingBookingId) {
      return;
    }

    // Find the highlighted booking
    const highlightedBooking = bookings.incoming.find(
      (b) => b.id === bookings.highlightedIncomingBookingId,
    );

    if (!highlightedBooking) {
      return;
    }

    // Check if we need to change the date
    const bookingDate = DateTime.fromISO(highlightedBooking.date);
    const currentSelectedDate = selectedDate
      ? DateTime.fromJSDate(selectedDate)
      : DateTime.local();

    if (!currentSelectedDate.hasSame(bookingDate, 'day')) {
      if (isToday(highlightedBooking.date)) {
        setSelectedDate(undefined); // Switch to today view
      } else {
        setSelectedDate(new Date(highlightedBooking.date));
      }
    }
  }, [bookings.highlightedIncomingBookingId, bookings.incoming, selectedDate, logger]);

  // Get real cheques from TableUniverse
  const tableUniverse = useTableUniverse();
  const allCheques = useMemo(() => {
    return Object.values(tableUniverse.cheques);
  }, [tableUniverse.cheques]);

  const bookingsToUse = selectedDate ? selectedDateBookings.bookings : bookings.today;

  // Keep selectedBooking in sync with latest data after refetch
  useEffect(() => {
    if (!selectedBooking) return;
    const updated = bookingsToUse.find((b) => b.id === selectedBooking.id);
    if (updated && updated !== selectedBooking) {
      setSelectedBooking(updated);
    }
  }, [bookingsToUse, selectedBooking]);

  // Auto-select newly created booking when it appears in the data
  useEffect(() => {
    if (!bookingToAutoSelect) return;

    const newBooking = bookingsToUse.find((b) => b.id === bookingToAutoSelect);
    if (newBooking) {
      logger.info('Auto-selecting newly created booking', { bookingId: bookingToAutoSelect });
      setSelectedBooking(newBooking);
      setSelectedCheque(null);
      setBookingToAutoSelect(null); // Clear the auto-select flag
    }
  }, [bookingsToUse, bookingToAutoSelect, logger]);

  // Scroll to highlighted booking after data loads
  useEffect(() => {
    if (!bookingToHighlight) {
      return;
    }

    if (!timelineContainerRef.current) {
      return;
    }

    // Wait for the DOM to update with the new booking data
    const scrollTimeout = setTimeout(() => {
      const bookingElement = timelineContainerRef.current?.querySelector(
        `[data-booking-id="${bookingToHighlight}"]`,
      );

      if (bookingElement) {
        logger.info('Scrolling to highlighted booking', { bookingId: bookingToHighlight });
        bookingElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });

        // Clear highlight after animation completes
        setTimeout(() => {
          setBookingToHighlight(null);
        }, 2000);
      } else {
        logger.warn('Booking element not found for scrolling', { bookingId: bookingToHighlight });
        // If element not found, clear highlight anyway to prevent infinite wait
        setBookingToHighlight(null);
      }
    }, 300); // Increased delay to ensure data is loaded and rendered

    return () => clearTimeout(scrollTimeout);
  }, [bookingsToUse, bookingToHighlight, logger]);

  // Transform and get accepted bookings from local state
  const acceptedBookings = useMemo(
    () => bookingsToUse.filter((booking) => booking.status === BookingStatus.ACCEPTED),
    [bookingsToUse],
  );

  const startedBookings = useMemo(
    () => bookingsToUse.filter((booking) => booking.status === BookingStatus.STARTED),
    [bookingsToUse],
  );

  // Get incoming bookings for the selected date
  const incomingBookingsForSelectedDate = useMemo(() => {
    // Use the selectedDate, if no selectedDate treat as today
    const selectedDay = DateTime.fromJSDate(selectedDate || new Date()).toFormat('yyyy-MM-dd');

    // Filter incoming bookings by selected date
    return bookings.incoming.filter((booking) => {
      const bookingDate = DateTime.fromISO(booking.date).toFormat('yyyy-MM-dd');
      return bookingDate === selectedDay;
    });
  }, [selectedDate, bookings.incoming]);

  const ongoingChequesForDate = useMemo(() => {
    const today = DateTime.local().startOf('day');

    // If selectedDate is undefined => treat as today
    const selectedDay = selectedDate ? DateTime.fromJSDate(selectedDate).startOf('day') : today;

    // Only show ongoing cheques if viewing today
    if (!selectedDay.hasSame(today, 'day')) {
      return [];
    }

    // Filter real cheques - get only active/open cheques with valid table IDs and timestamps
    const activeCheques = allCheques.filter((cheque) => {
      if (!cheque.tableId || !cheque.activeAt) return false;

      const chequeDt = DateTime.fromISO(cheque.activeAt);

      if (!chequeDt.isValid) return false;

      return chequeDt.hasSame(today, 'day');
    });

    return activeCheques;
  }, [selectedDate, allCheques]);

  // Calculate dynamic time range based on bookings, incoming bookings, and ongoing cheques
  const timeRange = useMemo(() => {
    return calculateDynamicTimeRange({
      acceptedBookings,
      incomingBookings: incomingBookingsForSelectedDate,
      ongoingChequesForDate,
    });
  }, [acceptedBookings, incomingBookingsForSelectedDate, ongoingChequesForDate]);

  // Generate time slots based on dynamic range
  const timeSlots = useMemo(() => {
    if (!timeRange) return [];

    const slots = [];
    for (let hour = timeRange.startHour; hour <= timeRange.endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < timeRange.endHour) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    return slots;
  }, [timeRange]);

  const onAcceptBooking = async (
    booking: GetIncomingBookings_operativeViewer_incomingBookingsForVenue,
  ) => {
    const now = DateTime.local();
    const selected = selectedDate ? DateTime.fromJSDate(selectedDate) : now;
    const bookingDate = DateTime.fromISO(booking.date);

    // Set the highlight first
    setBookingToHighlight(booking.id);

    // If the correct date is already selected, the scroll effect will handle it
    if (selected.hasSame(bookingDate, 'day')) {
      // Non-today dates aren't refetched by the provider, so refresh silently (no loader)
      if (!isToday(booking.date)) {
        await selectedDateBookings.refresh();
      }
      return;
    }

    // If the accepted booking is not for the currently selected date, switch to that date
    if (!isToday(booking.date)) {
      // If the booking date is not today, set the selected date to the booking date to show it in the timeline
      setSelectedDate(new Date(booking.date));
    } else {
      setSelectedDate(undefined); // Switch to today view to show the accepted booking
    }
  };

  const selectedChequeData = selectedCheque
    ? allCheques.find((c) => c.id === selectedCheque.chequeId)
    : undefined;

  // Check if the selected date is today
  const selectedIsToday = useMemo(() => {
    if (!selectedDate) return true; // If no date selected, treat as today

    return isToday(DateTime.fromJSDate(selectedDate).toFormat('yyyy-MM-dd'));
  }, [selectedDate]);

  // Get current time position for the indicator
  const currentTimePosition = useMemo(() => {
    if (!selectedIsToday) return null;
    return getCurrentTimePosition({ timeRange, currentTime });
  }, [selectedIsToday, currentTime, timeRange]);

  // Update Fuse collection with mapped guestNames when bookings change
  useEffect(() => {
    const entries: BookingSearchEntry[] = [...acceptedBookings, ...startedBookings]
      .filter((b) => b.guestName)
      .map((b) => ({ id: b.id, guestName: b.guestName! }));
    bookingFuse.current.setCollection(entries);
  }, [acceptedBookings, startedBookings]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        const results = bookingFuse.current
          .search(searchQuery.trim())
          .map((result) => result.item);
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Filter tables based on hideEmptyTables toggle, search query, and bookings
  const filteredTables = useMemo(() => {
    let result = tables;

    // Filter by search query using Fuse.js results
    if (searchQuery.trim().length > 0 && searchResults.length > 0) {
      const matchingBookingIds = new Set(searchResults.map((b) => b.id));
      result = result.filter((table) => {
        const allBookings = [...acceptedBookings, ...startedBookings];
        return allBookings.some(
          (b) => b.tableIds.includes(table.id) && matchingBookingIds.has(b.id),
        );
      });
    } else if (searchQuery.trim().length > 0 && searchResults.length === 0) {
      // Search is active but no results - show no tables
      result = [];
    }

    // Filter out tables that have no bookings and no ongoing cheques
    if (storage.hideEmptyTablesInBookings) {
      result = result.filter((table) => {
        const hasBookings = acceptedBookings.some((b) => b.tableIds.includes(table.id));
        const hasStartedBookings = startedBookings.some((b) => b.tableIds.includes(table.id));
        const hasCheques = ongoingChequesForDate.some((c) => c.tableId === table.id);
        const hasIncomingBookings = incomingBookingsForSelectedDate.some((b) =>
          b.tableIds.includes(table.id),
        );

        return hasBookings || hasStartedBookings || hasCheques || hasIncomingBookings;
      });
    }

    return result;
  }, [
    searchQuery,
    searchResults,
    storage.hideEmptyTablesInBookings,
    tables,
    acceptedBookings,
    startedBookings,
    ongoingChequesForDate,
    incomingBookingsForSelectedDate,
  ]);

  // Scroll to current time only on initial page load when viewing today's bookings
  useEffect(() => {
    // Only scroll if:
    // 1. We haven't scrolled yet (initial load)
    // 2. We're viewing today's bookings
    // 3. We have a valid position and container
    if (
      !hasScrolledToCurrentTime.current &&
      selectedIsToday &&
      currentTimePosition &&
      timelineContainerRef.current
    ) {
      // Small delay to ensure the DOM has fully rendered
      const scrollTimeout = setTimeout(() => {
        scrollToCurrentTime(timelineContainerRef, currentTimePosition);
        // Mark that we've performed the initial scroll
        hasScrolledToCurrentTime.current = true;
      }, 100);

      return () => clearTimeout(scrollTimeout);
    }
    // Return undefined when conditions aren't met
    return undefined;
  }, [selectedIsToday, currentTimePosition]);

  return (
    <OuterContainer display="flex">
      <Container>
        <Box display="flex" flexDirection="column" gap="small" marginBottom="small">
          <Box display="flex" alignItems="center" justifyContent="space-between" gap="small">
            <DateNavigationBox gap="small">
              <DateInput
                selectedDate={selectedDate || new Date()}
                setSelectedDate={(date) => {
                  bookings.actions.setHighlightedIncomingBooking(null); // Clear any highlighted incoming booking when changing date
                  if (date instanceof Date) {
                    if (isToday(DateTime.fromJSDate(date).toFormat('yyyy-MM-dd'))) {
                      setSelectedDate(undefined); // Treat as today
                    } else {
                      setSelectedDate(date);
                    }
                  }
                }}
                minDate={new Date()}
                isDark
                showArrows
              />
            </DateNavigationBox>

            <NewBooking
              onComplete={(bookingId, date) => {
                setBookingToHighlight(bookingId);
                setBookingToAutoSelect(bookingId); // Mark for auto-selection when data loads

                // Switch date picker to the booking's date so it's visible in the timeline
                if (isToday(date)) {
                  setSelectedDate(undefined);
                } else if (
                  selectedDate &&
                  DateTime.fromJSDate(selectedDate).toFormat('yyyy-MM-dd') === date
                ) {
                  // Same date already on screen: the date doesn't change, so nothing reloads it.
                  // Refresh silently so the new booking appears (and gets auto-selected) right away.
                  logger.info('Refreshing selected date after creating booking', {
                    bookingId,
                    date,
                  });
                  selectedDateBookings.refresh();
                } else {
                  setSelectedDate(new Date(date));
                }
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" gap="small">
            <SearchInputBox fluid>
              <Input
                fluid
                dark
                placeholder={i18n('Bookings.SearchByGuestName')}
                padding="small"
                isSearch
                id="booking-search"
                name="booking-search"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
              />
            </SearchInputBox>

            <Toggle
              label={i18n('Bookings.HideEmptyTables')}
              initialValue={storage.hideEmptyTablesInBookings}
              onToggle={(isActive) => {
                updateStorage({ hideEmptyTablesInBookings: isActive });
              }}
              lightText
            />
          </Box>
        </Box>

        {/* Timeline Grid */}
        {tables.length === 0 ? (
          <EmptyState>
            <Heading level={3} bottom={8} color="var(--colorBlueGray200)">
              {i18n('Bookings.NoTablesFound')}
            </Heading>
            <Paragraph color="var(--colorBlueGray200)" level={2}>
              {i18n('Bookings.ConfigureTablesMessage')}
            </Paragraph>
          </EmptyState>
        ) : bookings.todayLoading ||
          (selectedDateBookings.loading && bookingsToUse.length === 0) ? (
          <EmptyState>
            <Box justifyContent="center" display="flex">
              <Loader />
            </Box>
          </EmptyState>
        ) : !timeRange ? (
          <EmptyState>
            <Heading level={3} bottom={8} color="var(--colorBlueGray200)">
              {i18n('Bookings.NoAcceptedBookings')}
            </Heading>
          </EmptyState>
        ) : (
          <TimelineContainer ref={timelineContainerRef}>
            <TimelineScrollArea>
              <TimelineHeader columnCount={timeSlots.length}>
                <TableLabel>{i18n('Bookings.Table')}</TableLabel>
                {timeSlots.map((time) => (
                  <TimeSlot key={time}>{time}</TimeSlot>
                ))}
              </TimelineHeader>

              {/* Current time indicator - only shown when viewing today */}
              {currentTimePosition && (
                <CurrentTimeIndicator
                  gridColumn={currentTimePosition.gridColumn}
                  offsetPercentage={currentTimePosition.offsetPercentage}
                />
              )}

              {filteredTables.map((table) => (
                <TableRow
                  key={table.id}
                  table={table}
                  selectedChequeId={selectedCheque?.chequeId}
                  selectedBookingId={selectedBooking?.id}
                  timeSlots={timeSlots}
                  timeRange={timeRange}
                  bookingsForTable={bookingsToUse.filter((b) => {
                    if (!b.tableIds.includes(table.id)) return false;
                    // When search is active, only show matching bookings
                    if (searchQuery.trim().length > 0 && searchResults.length > 0) {
                      const matchingBookingIds = new Set(searchResults.map((r) => r.id));
                      return matchingBookingIds.has(b.id);
                    }
                    return true;
                  })}
                  incomingBookings={incomingBookingsForSelectedDate.filter((b) =>
                    b.tableIds.includes(table.id),
                  )}
                  ongoingCheques={ongoingChequesForDate.filter((c) => {
                    // Match cheque directly by tableId, or via a started booking's tableIds
                    const directMatch = c.tableId === table.id;
                    const bookingMatch = startedBookings.some(
                      (b) => b.chequeId === c.id && b.tableIds.includes(table.id),
                    );
                    if (!directMatch && !bookingMatch) return false;
                    // When search is active, only show cheques linked to matching started bookings
                    if (searchQuery.trim().length > 0) {
                      if (searchResults.length === 0) return false;
                      const matchingBookingIds = new Set(searchResults.map((r) => r.id));
                      return startedBookings.some(
                        (b) => b.chequeId === c.id && matchingBookingIds.has(b.id),
                      );
                    }
                    return true;
                  })}
                  onChequeClick={(chequeId, tableId) => {
                    setSelectedCheque({ chequeId, tableId });
                    setSelectedBooking(null);
                  }}
                  onBookingClick={(booking) => {
                    setSelectedBooking(booking);
                    setSelectedCheque(null);
                  }}
                  bookingToHighlight={bookingToHighlight}
                  onHighlightComplete={() => setBookingToHighlight(null)}
                  currentTime={selectedIsToday ? currentTime : undefined}
                />
              ))}
            </TimelineScrollArea>
          </TimelineContainer>
        )}
      </Container>

      {/* Sidebar: Show cheque details or incoming bookings */}
      <RightWrapper>
        {selectedCheque ? (
          <ChequeDetails
            chequeName={
              selectedChequeData?.name || '#' + selectedCheque.chequeId.slice(0, 4).toUpperCase()
            }
            tableName={(() => {
              const matchingBooking = startedBookings.find(
                (b) => b.chequeId === selectedCheque.chequeId,
              );
              if (matchingBooking && matchingBooking.tableIds.length > 1) {
                const names = tables
                  .filter((t) => matchingBooking.tableIds.includes(t.id))
                  .map((t) => t.code)
                  .join(', ');
                return names || 'Unknown';
              }
              return tables.find((t) => t.id === selectedCheque.tableId)?.code || 'Unknown';
            })()}
            tableId={selectedCheque.tableId}
            partySize={
              startedBookings.find((b) => b.chequeId === selectedCheque.chequeId)?.partySize ||
              selectedChequeData?.bookingData?.numberOfGuests ||
              selectedChequeData?.coverage ||
              0
            }
            startTime={
              allCheques.find((c) => c.id === selectedCheque.chequeId)?.activeAt ||
              new Date().toISOString()
            }
            booking={startedBookings.find((b) => b.chequeId === selectedCheque.chequeId)}
            chequeBookingData={selectedChequeData?.bookingData}
            onClose={() => setSelectedCheque(null)}
            onNavigateToTable={onNavigateToTable}
          />
        ) : selectedBooking ? (
          <BookingDetails
            booking={selectedBooking}
            tableName={
              selectedBooking.tableIds.length > 0
                ? tables
                    .filter((t) => selectedBooking.tableIds.includes(t.id))
                    .map((t) => t.code)
                    .join(', ')
                : undefined
            }
            tables={tables}
            onClose={() => setSelectedBooking(null)}
            onNavigateToTable={onNavigateToTable}
            onHighlightBooking={setBookingToHighlight}
            onBookingUpdated={async () => {
              // Refetch bookings for the current view to update the timeline.
              // Today's fetch lands in the Apollo cache, which the provider's query reads.
              if (selectedDate) {
                await selectedDateBookings.refresh();
              } else {
                await bookings.fetchBookingsForDate(DateTime.local().toFormat('yyyy-MM-dd'));
              }
            }}
          />
        ) : (
          <IncomingBookings onAcceptBooking={onAcceptBooking} />
        )}
      </RightWrapper>
    </OuterContainer>
  );
};

export default Bookings;
